import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
  XAxis, YAxis, Tooltip, ReferenceLine, ResponsiveContainer,
  Area, CartesianGrid, Line, ComposedChart, Scatter
} from "recharts";
import { HISTORY,FWDS,SURFACE,AS_OF,TENORS,EXPIRIES,EXPIRY_Y,EXPIRY_DAYS,nearestExpiry,STRIKES_BP,C,F,MONO,SANS,normCdf,normPdf,bachelier,bachelierDelta,bachelierVega,buildCubicSpline,buildMonotoneCubic,_splineCache,volAtStrike,volCubeInterp,buildCustomCube,dv01For,atmFwd,marketConeStrikes,priceLeg,payoffAtExpiry,netMetrics,payoffProfile,breakevens,strategyStats,rnDensity,probProfit,expectedPnl,_cubeCache,integrateDensity,readCube,snapToGrid,normalizeView,generateCandidates,priceLegs,computeSizing,buildUserPdf,integrateDist,evalUnderDist,moontowerCheck,isDegenerate,runPipeline,genStrategiesForTenor,displayStrike,WINDOW_DAYS,addDays,interpConeStrikes,RateChart,PayoffChart,SmilePanel,ExclusionRow,CURVE_STRATEGIES, } from './ratesShared';
import { S, btn, HEADER_H, PANEL_W } from './ui';

function nearestStrikeIdxAbs(cube, K_target){
  let best=0, bestD=Math.abs(cube.strikes_abs[0]-K_target);
  for (let i=1;i<cube.strikes_abs.length;i++){
    const d = Math.abs(cube.strikes_abs[i]-K_target);
    if (d < bestD){ best=i; bestD=d; }
  }
  return best;
}

// Build a normalised position (per "DV01-equivalent unit" of notional).
// Vol is interpolated at the exact requested strike via cubic spline across the smile
// and monotone cubic across expiry dates — no grid snapping.
function buildCurvePosition(strategyKey, shortTen, longTen, expiry, K_S_req, K_L_req){
  const cubeS = SURFACE[`${expiry}.${shortTen}`];
  const cubeL = SURFACE[`${expiry}.${longTen}`];
  if (!cubeS || !cubeL) return null;
  const F_S = cubeS.atm_fwd_bp, F_L = cubeL.atm_fwd_bp;
  const T     = EXPIRY_Y[expiry];
  const dv01_S = cubeS.dv01s[4];  // ATM dv01 (constant across strikes)
  const dv01_L = cubeL.dv01s[4];
  const def    = CURVE_STRATEGIES[strategyKey];
  // Cubic spline vol at exact requested strike; monotone cubic across expiry term structure
  const vol_S  = volCubeInterp(shortTen, T, K_S_req);
  const vol_L  = volCubeInterp(longTen,  T, K_L_req);
  // Bachelier premium, then normalise to per-DV01 units
  const prem_S = bachelier(F_S, K_S_req, T, vol_S, def.short.isPayer, dv01_S) / dv01_S;
  const prem_L = bachelier(F_L, K_L_req, T, vol_L, def.long.isPayer,  dv01_L) / dv01_L;
  const netPrem = def.short.side * prem_S + def.long.side * prem_L;
  const ratio   = dv01_L / dv01_S;  // curve-neutral DV01 sizing ratio
  return {
    strategyKey, shortTen, longTen, expiry,
    F_S, F_L, fwdSpread: F_L - F_S,
    K_S_req, K_L_req,
    K_S: K_S_req, K_L: K_L_req,
    offS_eff: K_S_req - F_S, offL_eff: K_L_req - F_L,
    dv01_S, dv01_L, ratio,
    prem_S, prem_L, netPrem,
    vol_S, vol_L,
    short: def.short, long: def.long, def,
    T,
    sigSpread: Math.sqrt(vol_S*vol_S + vol_L*vol_L) * Math.sqrt(T),
  };
}

// P&L at expiry per DV01-equivalent unit, in bp.
function curvePnlPerDV01(pos, rS, rL){
  const pS = pos.short.isPayer ? Math.max(rS - pos.K_S, 0) : Math.max(pos.K_S - rS, 0);
  const pL = pos.long.isPayer  ? Math.max(rL - pos.K_L, 0) : Math.max(pos.K_L - rL, 0);
  return pos.short.side * pS + pos.long.side * pL - pos.netPrem;
}

// Numerical breakeven: for each rS in the grid, find the spread where P&L crosses zero.
// P&L is piecewise-linear in (rS, spread) so at most two zeros per rS slice; we return the first.
function curveBreakevenCurve(pos, rSGrid, spreadGrid){
  const out = [];
  for (const rS of rSGrid){
    let prevPnl = null, prevSpr = null, found = null;
    for (const spr of spreadGrid){
      const pnl = curvePnlPerDV01(pos, rS, spr + rS);
      if (prevPnl !== null && prevPnl * pnl < 0){
        const t = prevPnl / (prevPnl - pnl);
        found = prevSpr + t * (spr - prevSpr);
        break;
      }
      prevPnl = pnl; prevSpr = spr;
    }
    out.push({ rS, spread: found });
  }
  return out;
}

function buildSpreadHistory(shortTen, longTen){
  return HISTORY.map(h => ({
    date: h.date, rS: h[shortTen], rL: h[longTen],
    spread: h[longTen] - h[shortTen],
  }));
}

function buildSpreadForwards(shortTen, longTen){
  return EXPIRIES.map(exp => {
    const fS = FWDS[`${exp}.${shortTen}`], fL = FWDS[`${exp}.${longTen}`];
    if (fS == null || fL == null) return null;
    return { expiry: exp, T: EXPIRY_Y[exp], fS, fL, spread: fL - fS };
  }).filter(Boolean);
}

// ----- HISTORICAL BACKTEST: conditional (option) vs linear (swap) -----
// At each historical entry date D, simulate: enter the same trade structure (same
// strategy and same offset-from-spot strikes) and hold to expiry T years later.
// Linear P&L per DV01 = spreadSign × (spread_exit − spread_entry) — the symmetric
// swap-pair payoff. Option P&L per DV01 = sum of leg payoffs minus net premium.
//
// Premium approximation: we hold today's net premium per DV01 constant across the
// backtest. This is honest given the data — we only have today's vol surface, and
// option premia are dominated by σ√T which doesn't change first-order with rate
// level. Different (and richer) vol regimes would shift premium up/down, but the
// shape of the saving distribution is governed by realised path, not premium drift.
function backtestCurveTrade(strategyKey, shortTen, longTen, T_years, offS, offL, premPerDV01, stepDays){
  const T_days = Math.round(T_years * 252);  // ~21 trading days/month
  const def = CURVE_STRATEGIES[strategyKey];
  const sign = def.spreadSign;
  const rows = [];
  const step = stepDays || 21;
  for (let i = 0; i + T_days < HISTORY.length; i += step){
    const entry = HISTORY[i], exit = HISTORY[i + T_days];
    if (entry[shortTen] == null || entry[longTen] == null
        || exit[shortTen] == null || exit[longTen] == null) continue;
    const rS_e = entry[shortTen], rL_e = entry[longTen];
    const rS_x = exit[shortTen],  rL_x = exit[longTen];
    const sp_e = rL_e - rS_e, sp_x = rL_x - rS_x;
    // Re-strike at entry to preserve the user's offset-from-forward intent
    const K_S = rS_e + offS, K_L = rL_e + offL;
    const pS = def.short.isPayer ? Math.max(rS_x - K_S, 0) : Math.max(K_S - rS_x, 0);
    const pL = def.long.isPayer  ? Math.max(rL_x - K_L, 0) : Math.max(K_L - rL_x, 0);
    const optPnl = def.short.side * pS + def.long.side * pL - premPerDV01;
    const linPnl = sign * (sp_x - sp_e);
    rows.push({
      entryDate: entry.date, exitDate: exit.date,
      sp_e, sp_x, dSp: sp_x - sp_e,
      rS_e, rL_e, rS_x, rL_x,
      K_S, K_L, optPnl, linPnl, saving: optPnl - linPnl,
    });
  }
  return rows;
}

function backtestStats(rows){
  if (!rows.length) return null;
  const sav = rows.map(r => r.saving);
  const sortedSav = [...sav].sort((a,b) => a-b);
  const median = sortedSav[Math.floor(sortedSav.length/2)];
  const mean = sav.reduce((a,b) => a+b, 0) / sav.length;
  const optWins = rows.filter(r => r.optPnl > 0).length;
  const linWins = rows.filter(r => r.linPnl > 0).length;
  const savPos = sav.filter(s => s > 0).length;
  return {
    count: rows.length,
    optWinRate: optWins/rows.length, linWinRate: linWins/rows.length,
    savingPosRate: savPos/rows.length,
    meanSaving: mean, medianSaving: median,
    maxSaving: Math.max(...sav), minSaving: Math.min(...sav),
    meanOpt: rows.reduce((a,r) => a+r.optPnl, 0)/rows.length,
    meanLin: rows.reduce((a,r) => a+r.linPnl, 0)/rows.length,
  };
}

// ----- STRIKE INPUT — text input with commit-on-blur, snap indicator, ATM button -----
function StrikeInput({ value, atm, snappedTo, onChange, label }){
  const [str, setStr] = useState(value != null ? value.toFixed(1) : '');
  useEffect(() => { setStr(value != null ? value.toFixed(1) : ''); }, [value]);
  const commit = () => {
    const v = parseFloat(str);
    if (!isNaN(v) && v > -500 && v < 500) onChange(v);
    else setStr(value != null ? value.toFixed(1) : '');
  };
  const offset = (value != null && atm != null) ? value - atm : null;
  const snapDelta = (value != null && snappedTo != null) ? snappedTo - value : null;
  const snapped = snapDelta != null && Math.abs(snapDelta) > 0.5;
  return (
    <div style={{display:'flex', flexDirection:'column', gap:2}}>
      <div style={{display:'flex', alignItems:'center', gap:6}}>
        <label style={{fontSize:F.sm, color:C.txD, minWidth:42}}>{label}</label>
        <input type="text" value={str}
               onChange={e => setStr(e.target.value)}
               onBlur={commit}
               onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
               style={{background:C.bg, border:`1px solid ${snapped ? C.am : C.bd}`, borderRadius:3,
                       padding:'3px 6px', color:C.tx, fontSize:F.lg, width:64,
                       fontFamily:'IBM Plex Mono, monospace', outline:'none'}}/>
        <span style={{fontSize:F.xs, color:C.txD}}>bp</span>
        {atm != null && (
          <button onClick={() => onChange(+atm.toFixed(1))} title="Reset to ATM"
            style={{background:'transparent', border:`1px solid ${C.bd}`, borderRadius:2,
                    padding:'1px 6px', color:C.txD, fontSize:F.xs, cursor:'pointer',
                    fontFamily:'IBM Plex Mono, monospace', fontWeight:600}}>ATM</button>
        )}
      </div>
      <div style={{fontSize:F.xs, color:C.txM, marginLeft:48, minHeight:11}}>
        {offset != null && Math.abs(offset) > 0.5 && (
          <span>{offset >= 0 ? 'ATM+' : 'ATM−'}{Math.abs(offset).toFixed(1)}bp</span>
        )}
        {offset != null && Math.abs(offset) <= 0.5 && (
          <span style={{color:C.gn}}>at ATM</span>
        )}
        {snapped && (
          <span style={{color:C.am, marginLeft:6}}>→ snaps to {snappedTo.toFixed(1)}</span>
        )}
        {!snapped && snapDelta != null && Math.abs(snapDelta) <= 0.5 && (
          <span style={{color:C.gn, marginLeft:6}}>✓ on grid</span>
        )}
      </div>
    </div>
  );
}

// ----- STAT BOX for backtest summary -----
function StatBox({ label, value, color }){
  return (
    <div style={{background:C.bg, border:`1px solid ${C.bd}`, borderRadius:3, padding:'6px 9px'}}>
      <div style={{fontSize:F.xxs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:2}}>
        {label}
      </div>
      <div style={{fontSize:F.h3, fontWeight:700, color, fontFamily:'IBM Plex Mono, monospace'}}>
        {value}
      </div>
    </div>
  );
}

// ----- BACKTEST TABLE -----
function CurveBacktestTable({ pos, dollarPerBpPnl }){
  const rows = useMemo(() => {
    if (!pos) return [];
    return backtestCurveTrade(
      pos.strategyKey, pos.shortTen, pos.longTen, pos.T,
      pos.offS_eff, pos.offL_eff, pos.netPrem
    );
  }, [pos]);
  const stats = useMemo(() => backtestStats(rows), [rows]);
  if (!pos) return null;
  if (!rows.length || !stats) return (
    <div style={{padding:'14px', fontSize:F.md, color:C.txM, fontStyle:'italic'}}>
      Not enough history for {pos.T*12}-month windows.
    </div>
  );
  // Most recent first, top 30
  const display = [...rows].reverse().slice(0, 30);
  const tMonths = Math.round(pos.T * 12);

  return (
    <div style={{padding:'12px 14px', borderTop:`1px solid ${C.bd}`}}>
      <div style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:8,
                    display:'flex', justifyContent:'space-between', alignItems:'baseline'}}>
        <span>Conditional vs linear — historical backtest</span>
        <span style={{color:C.txM, textTransform:'none', letterSpacing:0, fontStyle:'italic', fontSize:F.xs}}>
          {tMonths}-month windows · same strategy & offset · today's premium
          ({pos.netPrem >= 0 ? '−' : '+'}{Math.abs(pos.netPrem).toFixed(2)} bp/DV01) held constant
        </span>
      </div>

      {/* Stats grid */}
      <div style={{display:'grid', gridTemplateColumns:'repeat(4, 1fr)', gap:6, marginBottom:10}}>
        <StatBox label="Periods tested" value={`${stats.count}`} color={C.tx}/>
        <StatBox label="% saving > 0"
                 value={`${(stats.savingPosRate*100).toFixed(0)}%`}
                 color={stats.savingPosRate > 0.5 ? C.gn : C.rd}/>
        <StatBox label="Mean saving"
                 value={`${stats.meanSaving >= 0 ? '+' : ''}${stats.meanSaving.toFixed(1)} bp`}
                 color={stats.meanSaving >= 0 ? C.gn : C.rd}/>
        <StatBox label="Median saving"
                 value={`${stats.medianSaving >= 0 ? '+' : ''}${stats.medianSaving.toFixed(1)} bp`}
                 color={stats.medianSaving >= 0 ? C.gn : C.rd}/>
        <StatBox label="Best saving" value={`+${stats.maxSaving.toFixed(1)} bp`} color={C.gn}/>
        <StatBox label="Max regret" value={`${stats.minSaving.toFixed(1)} bp`} color={C.rd}/>
        <StatBox label="Option win %"
                 value={`${(stats.optWinRate*100).toFixed(0)}%`}
                 color={stats.optWinRate > stats.linWinRate ? C.gn : C.txD}/>
        <StatBox label="Linear win %"
                 value={`${(stats.linWinRate*100).toFixed(0)}%`}
                 color={stats.linWinRate > stats.optWinRate ? C.gn : C.txD}/>
      </div>

      {/* Mean-saving in dollar terms */}
      <div style={{display:'flex', justifyContent:'space-between', alignItems:'center',
                    background:C.bg, border:`1px solid ${C.bd}`, borderRadius:3,
                    padding:'6px 10px', marginBottom:8, fontSize:F.md}}>
        <span style={{color:C.txD, fontSize:F.sm, textTransform:'uppercase', letterSpacing:'.08em'}}>
          Mean saving at sized trade
        </span>
        <span className="mono" style={{color: stats.meanSaving >= 0 ? C.gn : C.rd, fontWeight:700, fontSize:F.xl}}>
          {stats.meanSaving >= 0 ? '+$' : '−$'}{Math.abs(stats.meanSaving * dollarPerBpPnl).toLocaleString('en-US',{maximumFractionDigits:0})} per trade
        </span>
      </div>

      {/* Table */}
      <div style={{overflow:'auto', maxHeight:280, border:`1px solid ${C.bd}`, borderRadius:3}}>
        <table style={{width:'100%', borderCollapse:'collapse', fontSize:F.md}}>
          <thead style={{background:C.bgC, position:'sticky', top:0, zIndex:1}}>
            <tr style={{borderBottom:`1px solid ${C.bd}`}}>
              {['Entry','Exit','Spread Δ','Linear P&L','Option P&L','Saving'].map((h,i) => (
                <th key={i} style={{padding:'6px 10px', textAlign:i<2?'left':'right', fontSize:F.xs,
                                     color:C.txD, textTransform:'uppercase', letterSpacing:'.08em',
                                     fontWeight:600, whiteSpace:'nowrap'}}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {display.map((r, i) => (
              <tr key={i} style={{borderBottom:`1px solid ${C.bd}`,
                                    background: i % 2 ? C.bg : 'transparent'}}>
                <td style={{padding:'5px 10px', color:C.txD, fontFamily:'IBM Plex Mono, monospace', fontSize:F.sm}}>
                  {r.entryDate}
                </td>
                <td style={{padding:'5px 10px', color:C.txD, fontFamily:'IBM Plex Mono, monospace', fontSize:F.sm}}>
                  {r.exitDate}
                </td>
                <td style={{padding:'5px 10px', textAlign:'right', fontFamily:'IBM Plex Mono, monospace',
                             color: r.dSp >= 0 ? C.gn : C.rd}}>
                  {r.dSp >= 0 ? '+' : ''}{r.dSp.toFixed(1)}
                </td>
                <td style={{padding:'5px 10px', textAlign:'right', fontFamily:'IBM Plex Mono, monospace',
                             color: r.linPnl >= 0 ? C.gn : C.rd, fontWeight:600}}>
                  {r.linPnl >= 0 ? '+' : ''}{r.linPnl.toFixed(1)}
                </td>
                <td style={{padding:'5px 10px', textAlign:'right', fontFamily:'IBM Plex Mono, monospace',
                             color: r.optPnl >= 0 ? C.gn : C.rd, fontWeight:600}}>
                  {r.optPnl >= 0 ? '+' : ''}{r.optPnl.toFixed(1)}
                </td>
                <td style={{padding:'5px 10px', textAlign:'right', fontFamily:'IBM Plex Mono, monospace',
                             color: r.saving >= 0 ? C.gn : C.rd, fontWeight:700}}>
                  {r.saving >= 0 ? '+' : ''}{r.saving.toFixed(1)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{fontSize:F.xs, color:C.txM, fontStyle:'italic', marginTop:6}}>
        Saving = option P&L − linear (DV01-weighted swap pair) P&L, in bp per DV01-unit. Positive = options were better.
        Strikes re-anchored at each entry to maintain the same offset from forward as your current trade.
        Showing most recent {display.length} of {rows.length} windows.
      </div>
    </div>
  );
}

// ----- SPREAD HISTORY CHART (with click-to-set-target, windowing, dense-grid forwards) -----
function SpreadHistoryChart({ shortTen, longTen, target, onDrag, fwdPoints, historyWindow, highlightDate, expiryDate }){
  const ref = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [hoverLvl, setHoverLvl] = useState(null);
  const lastHistDate = HISTORY[HISTORY.length-1].date;

  // Stitch windowed historical spread + forward curve.
  // Forwards built like RateChart: dense future-date grid, linearly interpolated
  // between forward nodes (today, 1M, 2M, 3M, 6M, 9M, 1Y), each anchored to its
  // exact tenor offset in days from the last historical date.
  const data = useMemo(() => {
    const fullHist = buildSpreadHistory(shortTen, longTen);
    const winDays  = WINDOW_DAYS[historyWindow] || WINDOW_DAYS['2Y'];
    const startIdx = Math.max(0, fullHist.length - winDays);
    const histRows = [];
    for (let i = startIdx; i < fullHist.length; i++){
      histRows.push({ d: fullHist[i].date, hist: fullHist[i].spread });
    }
    const last = histRows[histRows.length-1];
    if (!last) return [];

    // Forward nodes — today (current spread) plus each expiry's forward spread,
    // mapped to the day-offset of its tenor.
    const tenorDays = { '1M':30, '2M':60, '3M':91, '6M':182, '9M':274, '1Y':365 };
    const fwdNodes = [{ days: 0, fwd: last.hist }];
    for (const fp of fwdPoints){
      if (tenorDays[fp.expiry] != null) fwdNodes.push({ days: tenorDays[fp.expiry], fwd: fp.spread });
    }
    fwdNodes.sort((a,b) => a.days - b.days);

    // Dense future-date grid (every 5 days out to ~1Y), with exact-tenor anchor days marked
    const maxDays = 380;
    const futureDates = [];
    for (let d = 0; d <= maxDays; d += 5){
      futureDates.push({ d: addDays(lastHistDate, d), days: d });
    }
    for (const fp of fwdPoints){
      const dd = tenorDays[fp.expiry];
      if (dd == null) continue;
      const existing = futureDates.find(f => f.days === dd);
      if (existing) existing.tn = fp.expiry;
      else futureDates.push({ d: addDays(lastHistDate, dd), days: dd, tn: fp.expiry });
    }
    futureDates.sort((a,b) => a.days - b.days);

    const futRows = futureDates.map(f => {
      const row = { d: f.d };
      for (let i = 0; i < fwdNodes.length-1; i++){
        if (f.days >= fwdNodes[i].days && f.days <= fwdNodes[i+1].days){
          const w = (f.days - fwdNodes[i].days) / (fwdNodes[i+1].days - fwdNodes[i].days);
          row.fwd = fwdNodes[i].fwd + w * (fwdNodes[i+1].fwd - fwdNodes[i].fwd);
          break;
        }
      }
      if (row.fwd == null && f.days >= fwdNodes[fwdNodes.length-1].days){
        row.fwd = fwdNodes[fwdNodes.length-1].fwd;
      }
      if (f.tn) row.tn = f.tn;
      return row;
    });

    last.fwd = last.hist;  // anchor forward line to today's spot
    return [...histRows, ...futRows.filter(f => f.d > lastHistDate)];
  }, [shortTen, longTen, fwdPoints, lastHistDate, historyWindow]);

  const dom = useMemo(() => {
    const vals = [];
    for (const r of data){
      if (typeof r.hist === 'number') vals.push(r.hist);
      if (typeof r.fwd === 'number')  vals.push(r.fwd);
    }
    if (typeof target === 'number')   vals.push(target);
    if (!vals.length) return [-50, 100];
    const lo = Math.min(...vals), hi = Math.max(...vals);
    const pad = Math.max(8, (hi - lo) * 0.1);
    return [lo - pad, hi + pad];
  }, [data, target]);

  const getPoint = useCallback((e) => {
    const wrap = ref.current;
    if (!wrap) return null;
    const svg = wrap.querySelector('.recharts-wrapper');
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const top = 10, bottom = rect.height - 55;
    const yPx = e.clientY - rect.top - top;
    const ratio = 1 - yPx / (bottom - top);
    const lvl = dom[0] + ratio * (dom[1] - dom[0]);
    if (!isFinite(lvl)) return null;
    const gridEl = wrap.querySelector('.recharts-cartesian-grid');
    const gr = gridEl ? gridEl.getBoundingClientRect() : null;
    const plotLeft = gr ? gr.left : rect.left + 50, plotWidth = gr ? gr.width : rect.width - 60;
    const xRatio = Math.max(0, Math.min(1, (e.clientX - plotLeft) / plotWidth));
    const dataIdx = Math.max(0, Math.min(data.length-1, Math.round(xRatio*(data.length-1))));
    const date = data[dataIdx]?.d ?? null;
    return {
      level: Math.max(dom[0], Math.min(dom[1], +lvl.toFixed(2))),
      date, isFuture: date != null && date > lastHistDate,
    };
  }, [dom, data, lastHistDate]);

  const down = (e) => { e.preventDefault(); const p = getPoint(e); if (p!=null){ setDragging(true); setHoverLvl(p.level); onDrag(p.level, p.isFuture ? p.date : null); } };
  const move = (e) => { if (dragging){ const p = getPoint(e); if (p!=null){ setHoverLvl(p.level); onDrag(p.level, null); } } };
  const up   = () => setDragging(false);
  const leave= () => { if (dragging) setDragging(false); };

  return (
    <div ref={ref} onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={leave}
         style={{position:'relative', cursor: dragging?'ns-resize':'crosshair', userSelect:'none', height:240}}>
      {dragging && hoverLvl != null && (
        <div style={{position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', zIndex:10,
                     background:C.pu, color:C.bg, padding:'3px 10px', borderRadius:4,
                     fontFamily:'IBM Plex Mono, monospace', fontSize:F.h3, fontWeight:700}}>
          {hoverLvl.toFixed(1)}bp
        </div>
      )}
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={data} margin={{top:10, right:10, bottom:5, left:0}}>
          <defs>
            <linearGradient id="spG" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={C.pu} stopOpacity={0.18}/>
              <stop offset="95%" stopColor={C.pu} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={C.bd}/>
          <XAxis dataKey="d" tick={{fontSize:F.xs, fill:C.txD}} stroke={C.bd}
                 interval={Math.max(1, Math.floor(data.length/10))}
                 tickFormatter={(v) => {
                   if (typeof v !== 'string') return '';
                   const d = new Date(v);
                   const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
                   return `${m} ${String(d.getFullYear()%100).padStart(2,'0')}`;
                 }}
                 angle={-30} textAnchor="end" height={42}/>
          <YAxis domain={dom} tick={{fontSize:F.sm, fill:C.txD}} stroke={C.bd}
                 tickFormatter={(v) => v.toFixed(0)+'bp'} width={50} allowDataOverflow/>
          <Tooltip content={({active, payload, label}) => {
            if (!active || !payload || !payload.length) return null;
            const items = [];
            const labels = { hist: `${longTen}–${shortTen}`, fwd: 'Fwd spread' };
            const colors = { hist: C.pu, fwd: C.am };
            payload.forEach(p => {
              if (typeof p.value !== 'number' || !isFinite(p.value)) return;
              if (labels[p.dataKey])
                items.push({ name: labels[p.dataKey], val: p.value.toFixed(1)+'bp', color: colors[p.dataKey]||C.tx });
            });
            if (!items.length) return null;
            return (
              <div style={{background:C.bgC, border:`1px solid ${C.bd}`, borderRadius:6, padding:'6px 10px', fontSize:F.md, color:C.tx}}>
                <div style={{fontFamily:'IBM Plex Mono, monospace', color:C.txD, fontSize:F.sm, marginBottom:3}}>{label}</div>
                {items.map((it,i) => (
                  <div key={i} style={{display:'flex', justifyContent:'space-between', gap:12}}>
                    <span style={{color: it.color}}>{it.name}</span>
                    <span style={{fontFamily:'IBM Plex Mono, monospace', fontWeight:600}}>{it.val}</span>
                  </div>
                ))}
              </div>
            );
          }}/>
          <Area type="monotone" dataKey="hist" stroke={C.pu} fill="url(#spG)" strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls={false}/>
          <Line type="monotone" dataKey="fwd"  stroke={C.am} strokeWidth={2} strokeDasharray="6 3"
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  if (!payload?.tn || !cx || !cy) return null;
                  return <circle key={payload.tn} cx={cx} cy={cy} r={3} fill={C.am} stroke={C.bg} strokeWidth={1}/>;
                }}
                isAnimationActive={false} connectNulls={false}/>
          {/* Highlight marker — drawn as an invisible Line whose dots fire only at the hovered date.
              Using `dataKey="hist"` re-uses the existing Y-scale, so the dot lands on the actual series. */}
          {highlightDate && (
            <Line key={`hl-${highlightDate}`}
                  dataKey="hist" type="monotone"
                  stroke={C.pu} strokeOpacity={0} strokeWidth={0} isAnimationActive={false}
                  dot={(props) => {
                    const { cx, cy, payload } = props;
                    if (!payload || payload.d !== highlightDate || cx == null || cy == null) return null;
                    return <circle key="hlpt" cx={cx} cy={cy} r={5.5} fill="#e6edf3" stroke={C.pu} strokeWidth={2}/>;
                  }}
                  activeDot={false}/>
          )}
          <ReferenceLine key="today" x={lastHistDate} stroke={C.txM} strokeDasharray="2 2"
                         label={{ value:'TODAY', fill:C.txM, fontSize:F.xxs, position:'insideTopRight' }}/>
          {expiryDate && expiryDate > lastHistDate && (
            <ReferenceLine key={`expiry-${expiryDate}`} x={expiryDate} stroke={C.am} strokeDasharray="3 4" strokeOpacity={0.3}/>
          )}
          {target != null && <ReferenceLine key={`target-${target.toFixed(3)}`} y={target} stroke={C.pu} strokeWidth={2} strokeDasharray="6 3"
                                             label={{ value: `◆ ${target.toFixed(1)}`, fill:C.pu, fontSize:F.md, fontWeight:700, position:'right' }}/>}
          {/* Cross-chart hovered date vertical guide */}
          {highlightDate && (
            <ReferenceLine key={`hlv-${highlightDate}`} x={highlightDate}
                           stroke="#e6edf3" strokeWidth={1} strokeOpacity={0.5}/>
          )}
          {dragging && hoverLvl != null && <ReferenceLine key="hover" y={hoverLvl} stroke={C.am} strokeWidth={1} strokeDasharray="2 2" strokeOpacity={0.6}/>}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ----- SINGLE-TENOR RATE HISTORY CHART (click to set strike) -----
// Mirrors SpreadHistoryChart's structure but for one tenor at a time, with the
// strike line drawn at the absolute level (not the offset). The forward marker
// for the active expiry is highlighted to make "where the strike sits relative
// to the forward" obvious.
function RateHistoryChart({ tenor, strikeAbs, fwdAbs, expiry, onDragStrike, historyWindow, accent, highlightDate, expiryDate }){
  const ref = useRef(null);
  const [dragging, setDragging] = useState(false);
  const [hoverLvl, setHoverLvl] = useState(null);
  const lastHistDate = HISTORY[HISTORY.length-1].date;

  const data = useMemo(() => {
    // Windowed history of the chosen tenor
    const winDays  = WINDOW_DAYS[historyWindow] || WINDOW_DAYS['2Y'];
    const startIdx = Math.max(0, HISTORY.length - winDays);
    const histRows = [];
    for (let i = startIdx; i < HISTORY.length; i++){
      histRows.push({ d: HISTORY[i].date, hist: HISTORY[i][tenor] });
    }
    const last = histRows[histRows.length-1];
    if (!last) return [];

    // Forward nodes for this tenor: today (current rate) + each expiry's forward
    const tenorDays = { '1M':30, '2M':60, '3M':91, '6M':182, '9M':274, '1Y':365 };
    const fwdNodes = [{ days: 0, fwd: last.hist }];
    for (const e of EXPIRIES){
      const fv = FWDS[`${e}.${tenor}`];
      if (fv != null && tenorDays[e] != null) fwdNodes.push({ days: tenorDays[e], fwd: fv, tn: e });
    }
    fwdNodes.sort((a,b) => a.days - b.days);

    const maxDays = 380;
    const futureDates = [];
    for (let d = 0; d <= maxDays; d += 5){
      futureDates.push({ d: addDays(lastHistDate, d), days: d });
    }
    for (const e of EXPIRIES){
      const dd = tenorDays[e];
      if (dd == null) continue;
      const existing = futureDates.find(f => f.days === dd);
      if (existing) existing.tn = e;
      else futureDates.push({ d: addDays(lastHistDate, dd), days: dd, tn: e });
    }
    futureDates.sort((a,b) => a.days - b.days);

    const futRows = futureDates.map(f => {
      const row = { d: f.d };
      for (let i = 0; i < fwdNodes.length-1; i++){
        if (f.days >= fwdNodes[i].days && f.days <= fwdNodes[i+1].days){
          const w = (f.days - fwdNodes[i].days) / (fwdNodes[i+1].days - fwdNodes[i].days);
          row.fwd = fwdNodes[i].fwd + w * (fwdNodes[i+1].fwd - fwdNodes[i].fwd);
          break;
        }
      }
      if (row.fwd == null && f.days >= fwdNodes[fwdNodes.length-1].days){
        row.fwd = fwdNodes[fwdNodes.length-1].fwd;
      }
      if (f.tn) row.tn = f.tn;
      return row;
    });

    last.fwd = last.hist;
    return [...histRows, ...futRows.filter(f => f.d > lastHistDate)];
  }, [tenor, lastHistDate, historyWindow]);

  const dom = useMemo(() => {
    const vals = [];
    for (const r of data){
      if (typeof r.hist === 'number') vals.push(r.hist);
      if (typeof r.fwd === 'number')  vals.push(r.fwd);
    }
    if (typeof strikeAbs === 'number') vals.push(strikeAbs);
    if (!vals.length) return [-50, 100];
    const lo = Math.min(...vals), hi = Math.max(...vals);
    const pad = Math.max(8, (hi - lo) * 0.1);
    return [lo - pad, hi + pad];
  }, [data, strikeAbs]);

  const getPoint = useCallback((e) => {
    const wrap = ref.current;
    if (!wrap) return null;
    const svg = wrap.querySelector('.recharts-wrapper');
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    const top = 10, bottom = rect.height - 55;
    const yPx = e.clientY - rect.top - top;
    const ratio = 1 - yPx / (bottom - top);
    const lvl = dom[0] + ratio * (dom[1] - dom[0]);
    if (!isFinite(lvl)) return null;
    const gridEl = wrap.querySelector('.recharts-cartesian-grid');
    const gr = gridEl ? gridEl.getBoundingClientRect() : null;
    const plotLeft = gr ? gr.left : rect.left + 40, plotWidth = gr ? gr.width : rect.width - 50;
    const xRatio = Math.max(0, Math.min(1, (e.clientX - plotLeft) / plotWidth));
    const idx = Math.max(0, Math.min(data.length-1, Math.round(xRatio*(data.length-1))));
    const date = data[idx]?.d ?? null;
    return {
      level: Math.max(dom[0], Math.min(dom[1], +lvl.toFixed(2))),
      date, isFuture: date != null && date > lastHistDate,
    };
  }, [dom, data, lastHistDate]);

  const down = (e) => { e.preventDefault(); const p = getPoint(e); if (p!=null){ setDragging(true); setHoverLvl(p.level); onDragStrike(p.level, p.isFuture ? p.date : null); } };
  const move = (e) => { if (dragging){ const p = getPoint(e); if (p!=null){ setHoverLvl(p.level); onDragStrike(p.level, null); } } };
  const up   = () => setDragging(false);
  const leave= () => { if (dragging) setDragging(false); };

  return (
    <div ref={ref} onMouseDown={down} onMouseMove={move} onMouseUp={up} onMouseLeave={leave}
         style={{position:'relative', cursor: dragging?'ns-resize':'crosshair', userSelect:'none', height:240}}>
      {dragging && hoverLvl != null && (
        <div style={{position:'absolute', right:12, top:'50%', transform:'translateY(-50%)', zIndex:10,
                     background:accent, color:C.bg, padding:'3px 10px', borderRadius:4,
                     fontFamily:'IBM Plex Mono, monospace', fontSize:F.h3, fontWeight:700}}>
          {hoverLvl.toFixed(1)}bp
        </div>
      )}
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={data} margin={{top:10, right:10, bottom:5, left:0}}>
          <defs>
            <linearGradient id={`rtG_${tenor}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={accent} stopOpacity={0.18}/>
              <stop offset="95%" stopColor={accent} stopOpacity={0}/>
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={C.bd}/>
          <XAxis dataKey="d" tick={{fontSize:F.xs, fill:C.txD}} stroke={C.bd}
                 interval={Math.max(1, Math.floor(data.length/10))}
                 tickFormatter={(v) => {
                   if (typeof v !== 'string') return '';
                   const d = new Date(v);
                   const m = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][d.getMonth()];
                   return `${m} ${String(d.getFullYear()%100).padStart(2,'0')}`;
                 }}
                 angle={-30} textAnchor="end" height={42}/>
          <YAxis domain={dom} tick={{fontSize:F.sm, fill:C.txD}} stroke={C.bd}
                 tickFormatter={(v) => v.toFixed(0)+'bp'} width={50} allowDataOverflow/>
          <Tooltip content={({active, payload, label}) => {
            if (!active || !payload || !payload.length) return null;
            const items = [];
            const labels = { hist: tenor, fwd: 'Forward' };
            const colors = { hist: accent, fwd: C.am };
            payload.forEach(p => {
              if (typeof p.value !== 'number' || !isFinite(p.value)) return;
              if (labels[p.dataKey])
                items.push({ name: labels[p.dataKey], val: p.value.toFixed(1)+'bp', color: colors[p.dataKey]||C.tx });
            });
            if (!items.length) return null;
            return (
              <div style={{background:C.bgC, border:`1px solid ${C.bd}`, borderRadius:6, padding:'6px 10px', fontSize:F.md, color:C.tx}}>
                <div style={{fontFamily:'IBM Plex Mono, monospace', color:C.txD, fontSize:F.sm, marginBottom:3}}>{label}</div>
                {items.map((it,i) => (
                  <div key={i} style={{display:'flex', justifyContent:'space-between', gap:12}}>
                    <span style={{color: it.color}}>{it.name}</span>
                    <span style={{fontFamily:'IBM Plex Mono, monospace', fontWeight:600}}>{it.val}</span>
                  </div>
                ))}
              </div>
            );
          }}/>
          <Area type="monotone" dataKey="hist" stroke={accent} fill={`url(#rtG_${tenor})`} strokeWidth={1.5} dot={false} isAnimationActive={false} connectNulls={false}/>
          <Line type="monotone" dataKey="fwd"  stroke={C.am} strokeWidth={2} strokeDasharray="6 3"
                dot={(props) => {
                  const { cx, cy, payload } = props;
                  if (!payload?.tn || !cx || !cy) return null;
                  // Highlight the active expiry's node
                  const active = payload.tn === expiry;
                  return <circle key={payload.tn} cx={cx} cy={cy} r={active?5:3}
                                 fill={active?C.am:C.bg} stroke={C.am}
                                 strokeWidth={active?2:1}/>;
                }}
                isAnimationActive={false} connectNulls={false}/>
          {/* Cross-chart highlight dot on the historical series */}
          {highlightDate && (
            <Line key={`hl-${highlightDate}`}
                  dataKey="hist" type="monotone"
                  stroke={accent} strokeOpacity={0} strokeWidth={0} isAnimationActive={false}
                  dot={(props) => {
                    const { cx, cy, payload } = props;
                    if (!payload || payload.d !== highlightDate || cx == null || cy == null) return null;
                    return <circle key="hlpt" cx={cx} cy={cy} r={5.5} fill="#e6edf3" stroke={accent} strokeWidth={2}/>;
                  }}
                  activeDot={false}/>
          )}
          <ReferenceLine key="today" x={lastHistDate} stroke={C.txM} strokeDasharray="2 2"
                         label={{ value:'TODAY', fill:C.txM, fontSize:F.xxs, position:'insideTopRight' }}/>
          {/* Expiry date vertical guide */}
          {expiryDate && expiryDate > lastHistDate && (
            <ReferenceLine key={`expiry-${expiryDate}`} x={expiryDate} stroke={C.am} strokeDasharray="3 4" strokeOpacity={0.3}/>
          )}
          {/* Forward marker — rendered before strike so strike appears on top when at same level */}
          {fwdAbs != null && <ReferenceLine key="fwd"
                                            y={fwdAbs} stroke={C.am} strokeDasharray="2 4" strokeOpacity={0.6}
                                            label={{ value: `F ${fwdAbs.toFixed(1)}`, fill:C.am, fontSize:F.sm, position:'insideTopLeft' }}/>}
          {/* Strike line — solid, rendered after forward so it's always visible on top */}
          {strikeAbs != null && <ReferenceLine key="strike"
                                                y={strikeAbs} stroke={accent} strokeWidth={2.5}
                                                label={{ value: `K ${strikeAbs.toFixed(1)}`, fill:accent, fontSize:F.md, fontWeight:700, position:'insideBottomLeft' }}
                                                allowDataOverflow={true}/>}
          {/* Cross-chart hovered-date vertical guide */}
          {highlightDate && (
            <ReferenceLine key={`hlv-${highlightDate}`} x={highlightDate}
                           stroke="#e6edf3" strokeWidth={1} strokeOpacity={0.5}/>
          )}
          {dragging && hoverLvl != null && <ReferenceLine key="hover" y={hoverLvl} stroke={accent} strokeWidth={1} strokeDasharray="2 2" strokeOpacity={0.6}/>}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ----- SCATTER + P&L FRONTIER CHART -----
// X = short rate at expiry, Y = spread (long − short). Historical points show the joint
// distribution; overlaid lines: K_S (vertical), K_L diagonal (spread = K_L − rS),
// breakeven curve (P&L = 0), and target marker at (forward_S, target_spread).
// Viridis-ish perceptually-uniform colormap, 9 anchors from oldest (dark purple)
// to newest (yellow). Lightweight linear interp in RGB — close enough to the real
// viridis for the purpose of showing time-order on a scatter.
const VIRIDIS_STOPS = [
  [ 68,  1, 84],  // 0.000  deep purple
  [ 72, 35,116],  // 0.125
  [ 64, 67,135],  // 0.250
  [ 52, 94,141],  // 0.375
  [ 41,120,142],  // 0.500  teal
  [ 32,144,140],
  [ 34,167,132],
  [ 68,190,112],
  [121,209, 81],  // 0.875
  [253,231, 37],  // 1.000  yellow
];
function viridis(t){
  if (!isFinite(t)) return 'rgb(120,120,120)';
  const tc = Math.max(0, Math.min(1, t));
  const seg = tc * (VIRIDIS_STOPS.length - 1);
  const i = Math.min(VIRIDIS_STOPS.length - 2, Math.floor(seg));
  const w = seg - i;
  const a = VIRIDIS_STOPS[i], b = VIRIDIS_STOPS[i+1];
  const r = Math.round(a[0] + w*(b[0]-a[0]));
  const g = Math.round(a[1] + w*(b[1]-a[1]));
  const bl= Math.round(a[2] + w*(b[2]-a[2]));
  return `rgb(${r},${g},${bl})`;
}

// Custom dot renderer for Recharts Scatter. Has to be a function so it can
// attach per-point mouse handlers — Scatter's built-in tooltip fires on
// hover-area events, not per-dot.
function makeDotRenderer({ onHover, highlightDate, hoverDate }){
  return (props) => {
    const { cx, cy, payload } = props;
    if (cx == null || cy == null || !payload) return null;
    const isHover = hoverDate && payload.date === hoverDate;
    const isHighlight = highlightDate && payload.date === highlightDate;
    const r = isHover ? 5 : (isHighlight ? 4.5 : 2.5);
    const strokeW = (isHover || isHighlight) ? 1.5 : 0;
    return (
      <circle
        cx={cx} cy={cy} r={r}
        fill={payload.color || '#888'}
        fillOpacity={isHover ? 1 : (isHighlight ? 0.95 : 0.65)}
        stroke={(isHover || isHighlight) ? '#e6edf3' : 'none'}
        strokeWidth={strokeW}
        style={{cursor:'pointer', pointerEvents:'all'}}
        onMouseEnter={onHover ? () => onHover(payload.date) : undefined}
        onMouseLeave={onHover ? () => onHover(null)          : undefined}
      />
    );
  };
}

function CurveScatterChart({ pos, history, targetSpread, onHoverDate, highlightDate }){
  const [hoverDate, setHoverDate] = useState(null);
  const [yMode, setYMode] = useState('curve'); // 'curve' = spread, 'long' = absolute long tenor rate
  const onDotHover = useCallback((d) => {
    setHoverDate(d);
    if (onHoverDate) onHoverDate(d);
  }, [onHoverDate]);

  const colouredHist = useMemo(() => {
    if (!history.length) return [];
    const N = history.length;
    return history.map((h, i) => ({
      ...h,
      t: N > 1 ? i / (N - 1) : 1,
      color: viridis(N > 1 ? i / (N - 1) : 1),
    }));
  }, [history]);

  // Domain — depends on yMode
  const dom = useMemo(() => {
    if (!pos || !colouredHist.length) return { rS:[-100,100], y:[-100,200] };
    const rSs = colouredHist.map(h => h.rS).concat([pos.F_S]);
    const ys = yMode === 'curve'
      ? colouredHist.map(h => h.spread).concat([pos.fwdSpread])
      : colouredHist.map(h => h.rL).concat([pos.F_L]);
    if (targetSpread != null) ys.push(yMode === 'curve' ? targetSpread : pos.F_S + targetSpread);
    const rPad = Math.max(15, (Math.max(...rSs) - Math.min(...rSs)) * 0.1);
    const yPad = Math.max(15, (Math.max(...ys) - Math.min(...ys)) * 0.1);
    return {
      rS: [Math.min(...rSs) - rPad, Math.max(...rSs) + rPad],
      y:  [Math.min(...ys) - yPad, Math.max(...ys) + yPad],
    };
  }, [pos, colouredHist, targetSpread, yMode]);

  // Breakeven curve & K_L line — stored with a generic `y` field for the current mode.
  const overlays = useMemo(() => {
    if (!pos) return { be:[], kL:[] };
    const N = 60;
    const rSGrid = Array.from({length:N}, (_,i) => dom.rS[0] + (i/(N-1)) * (dom.rS[1] - dom.rS[0]));
    const M = 200;
    // Spread grid spans the spread range implied by the current y domain
    const sprLo = yMode === 'curve' ? dom.y[0] : dom.y[0] - dom.rS[1];
    const sprHi = yMode === 'curve' ? dom.y[1] : dom.y[1] - dom.rS[0];
    const sprGrid = Array.from({length:M}, (_,i) => sprLo + (i/(M-1)) * (sprHi - sprLo));
    const beRaw = curveBreakevenCurve(pos, rSGrid, sprGrid).filter(p => p.spread != null);
    // Transform to display-y: spread mode keeps spread; long mode converts to absolute rL
    const be = beRaw.map(p => ({ rS: p.rS, y: yMode === 'curve' ? p.spread : p.rS + p.spread }));
    // K_L diagonal: spread = K_L − rS → in long mode rL = K_L (constant horizontal)
    const kL = rSGrid.map(rS => ({ rS, y: yMode === 'curve' ? pos.K_L - rS : pos.K_L }));
    return { be, kL };
  }, [pos, dom, yMode]);

  // Determine which side of the breakeven curve has positive PnL
  const positiveIsAbove = useMemo(() => {
    if (!pos) return true;
    // Test at max y — if positive there, positive region is above breakeven
    const testSpread = yMode === 'curve' ? dom.y[1] : dom.y[1] - pos.F_S;
    return curvePnlPerDV01(pos, pos.F_S, pos.F_S + testSpread) > 0;
  }, [pos, dom, yMode]);

  // Target marker — needs both fields so it works in either y mode
  const targetPoint = (pos && targetSpread != null) ? [{
    rS: pos.F_S,
    spread: targetSpread,
    rL: pos.F_S + targetSpread,
  }] : [];
  const targetPnl = (pos && targetSpread != null)
    ? curvePnlPerDV01(pos, pos.F_S, pos.F_S + targetSpread) : null;

  // Forward Y reference value
  const fwdY = pos ? (yMode === 'curve' ? pos.fwdSpread : pos.F_L) : null;

  const dateRange = useMemo(() => {
    if (!colouredHist.length) return null;
    return { first: colouredHist[0].date, last: colouredHist[colouredHist.length-1].date };
  }, [colouredHist]);

  const yDataKey  = yMode === 'curve' ? 'spread' : 'rL';
  const yAxisLabel = yMode === 'curve'
    ? `spread ${pos?.longTen||''}−${pos?.shortTen||''} (bp)`
    : `${pos?.longTen||'long'} rate (bp)`;

  return (
    <div>
      {/* Y-axis mode toggle */}
      <div style={{display:'flex', gap:4, alignItems:'center', padding:'0 8px 4px 56px'}}>
        <span style={{fontSize:F.xxs, color:C.txD, textTransform:'uppercase', letterSpacing:'.06em'}}>Y axis:</span>
        {[['curve','Spread'], ['long', pos?.longTen||'Long rate']].map(([m, l]) => (
          <button key={m} onClick={() => setYMode(m)}
                  style={{background: m===yMode ? C.acD : 'transparent',
                          border:`1px solid ${m===yMode ? C.ac : C.bd}`,
                          borderRadius:3, padding:'1px 7px', fontSize:F.xxs, cursor:'pointer',
                          color: m===yMode ? C.ac : C.txD,
                          fontFamily:'IBM Plex Mono, monospace', fontWeight:600}}>
            {l}
          </button>
        ))}
      </div>
      <div style={{position:'relative', height:340}}>
        <ResponsiveContainer width="100%" height={340}>
          <ComposedChart margin={{top:10, right:14, bottom:30, left:0}}
                         onMouseLeave={() => onDotHover(null)}>
            <CartesianGrid strokeDasharray="3 3" stroke={C.bd}/>
            <XAxis type="number" dataKey="rS" domain={dom.rS} allowDataOverflow
                   tick={{fontSize:F.sm, fill:C.txD}} stroke={C.bd}
                   tickFormatter={v => v.toFixed(0)+'bp'}
                   label={{ value:`${pos?.shortTen||''} rate at expiry (bp)`, position:'insideBottom', offset:-5, fill:C.txD, fontSize:F.sm }}/>
            <YAxis type="number" dataKey={yDataKey} domain={dom.y} allowDataOverflow
                   tick={{fontSize:F.sm, fill:C.txD}} stroke={C.bd}
                   tickFormatter={v => v.toFixed(0)+'bp'} width={50}
                   label={{ value:yAxisLabel, angle:-90, position:'insideLeft', fill:C.txD, fontSize:F.sm }}/>
            <Tooltip content={() => null} cursor={false}/>
            {/* Faint green fill for the positive-PnL region (above or below breakeven) */}
            {pos && overlays.be.length > 1 && (
              <Area data={overlays.be} type="monotone" dataKey="y"
                    stroke="none" fill={C.gn} fillOpacity={0.07}
                    baseValue={positiveIsAbove ? dom.y[1] : dom.y[0]}
                    isAnimationActive={false} dot={false} legendType="none"/>
            )}
            {/* Historical scatter — viridis coloured, date on hover */}
            <Scatter data={colouredHist}
                     shape={makeDotRenderer({ onHover: onDotHover, highlightDate, hoverDate })}
                     isAnimationActive={false}/>
            {/* K_L: diagonal in spread mode, horizontal at K_L in long mode */}
            {pos && <Line data={overlays.kL} dataKey="y" type="linear"
                          stroke={C.txD} strokeWidth={1} strokeDasharray="3 4"
                          dot={false} isAnimationActive={false} legendType="none"/>}
            {/* Breakeven (P&L = 0) — strategy colour */}
            {pos && <Line data={overlays.be} dataKey="y" type="monotone"
                          stroke={pos.def.color} strokeWidth={2}
                          dot={false} isAnimationActive={false} legendType="none"/>}
            {/* Strike & forward reference lines */}
            {pos && <ReferenceLine x={pos.K_S} stroke={C.am} strokeDasharray="4 3" strokeOpacity={0.7}
                                   label={{ value:`K_${pos.shortTen}=${pos.K_S.toFixed(1)}`, fill:C.am, fontSize:F.sm, position:'insideTopLeft' }}/>}
            {pos && <ReferenceLine x={pos.F_S} stroke={C.txM} strokeDasharray="2 4" strokeOpacity={0.8}
                                   label={{ value:`F_${pos.shortTen}=${pos.F_S.toFixed(1)}`, fill:C.txM, fontSize:F.xs, position:'insideBottomLeft' }}/>}
            {fwdY != null && <ReferenceLine y={fwdY} stroke={C.txM} strokeDasharray="2 4" strokeOpacity={0.8}
                                   label={{ value: yMode === 'curve' ? `fwd ${fwdY.toFixed(1)}` : `F_${pos.longTen}=${fwdY.toFixed(1)}`, fill:C.txM, fontSize:F.xs, position:'insideBottomRight' }}/>}
            {/* Target marker — diamond, coloured by sign of P&L */}
            {targetPoint.length > 0 && <Scatter data={targetPoint}
                                                fill={targetPnl>=0?C.gn:C.rd} stroke={C.bg} strokeWidth={1.5}
                                                shape="diamond" isAnimationActive={false} r={8}/>}
          </ComposedChart>
        </ResponsiveContainer>

        {/* Viridis legend strip */}
        {dateRange && (
          <div style={{position:'absolute', right:18, bottom:38, display:'flex', flexDirection:'column',
                        alignItems:'flex-end', pointerEvents:'none'}}>
            <div style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:2}}>Time</div>
            <div style={{width:140, height:8, borderRadius:2,
                         background:`linear-gradient(to right, ${VIRIDIS_STOPS.map(c=>`rgb(${c[0]},${c[1]},${c[2]})`).join(',')})`,
                         border:`1px solid ${C.bd}`}}/>
            <div style={{display:'flex', justifyContent:'space-between', width:140, fontSize:F.xs,
                          color:C.txD, marginTop:1, fontFamily:'IBM Plex Mono, monospace'}}>
              <span>{dateRange.first}</span>
              <span>{dateRange.last}</span>
            </div>
          </div>
        )}

        {/* Hover readout */}
        {hoverDate && (() => {
          const row = colouredHist.find(h => h.date === hoverDate);
          if (!row || !pos) return null;
          const pnl = curvePnlPerDV01(pos, row.rS, row.spread + row.rS);
          return (
            <div style={{position:'absolute', right:12, top:12,
                          background:C.bgC, border:`1px solid ${C.bd}`,
                          borderRadius:6, padding:'6px 10px',
                          fontSize:F.md, color:C.tx, fontFamily:'IBM Plex Mono, monospace',
                          pointerEvents:'none', minWidth:160}}>
              <div style={{color:C.txD, fontSize:F.sm, marginBottom:3}}>{row.date}</div>
              <div>{pos.shortTen} = <span style={{color:C.tx, fontWeight:600}}>{row.rS.toFixed(1)}bp</span></div>
              <div>{pos.longTen} = <span style={{color:C.tx, fontWeight:600}}>{(row.rS + row.spread).toFixed(1)}bp</span></div>
              <div>spread = <span style={{color:C.pu, fontWeight:700}}>{row.spread.toFixed(1)}bp</span></div>
              <div style={{marginTop:3, paddingTop:3, borderTop:`1px dashed ${C.bd}`,
                            color: pnl>=0?C.gn:C.rd, fontWeight:700}}>
                P&L = {pnl>=0?'+':''}{pnl.toFixed(2)} bp/DV01
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ----- MAIN CURVE APP -----
export default function CurveApp(){
  const [shortTen,  setShortTen]  = useState('2Y');
  const [longTen,   setLongTen]   = useState('10Y');
  const [strategy,  setStrategy]  = useState('bull_steep');
  const [expiry,    setExpiry]    = useState('6M');
  const [K_S_req,   setK_S_req]   = useState(null);   // absolute strike, bp
  const [K_L_req,   setK_L_req]   = useState(null);
  const [notionalL, setNotionalL] = useState(100);    // millions of long-tenor swaption
  const [target,    setTarget]    = useState(null);
  const [customExpDate, setCustomExpDate] = useState(''); // exact clicked date for faint line
  const [historyWindow, setHistoryWindow] = useState('2Y');
  const [legView,   setLegView]   = useState('short'); // which tenor history+strike to show
  const [expDateLocked, setExpDateLocked] = useState(false);
  const expFromClick = useRef(false); // suppress target reset when expiry set from chart click

  // Right chart → strike only. Expiry is locked to whatever the left chart set.
  const onDragStrike = useCallback((absRate) => {
    if (legView === 'short'){
      const newK_S = +absRate.toFixed(2);
      setK_S_req(newK_S);
      if (target != null) setK_L_req(+(newK_S + target).toFixed(2));
    } else {
      const newK_L = +absRate.toFixed(2);
      setK_L_req(newK_L);
      if (target != null) setK_S_req(+(newK_L - target).toFixed(2));
    }
  }, [legView, target]);

  // Left (spread) chart → target spread. Clicking in the future also sets the expiry.
  //   legView='short': K_L = F_S + spread
  //   legView='long':  K_S = F_L − spread
  const onDragSpread = useCallback((spreadLvl, clickDate) => {
    setTarget(spreadLvl);
    if (clickDate && !expDateLocked) {
      setCustomExpDate(clickDate);
      const newExp = nearestExpiry(clickDate, HISTORY[HISTORY.length-1].date, EXPIRIES);
      if (newExp) {
        expFromClick.current = true;
        setExpiry(newExp);
      }
      // Let the K-reset useEffect handle strikes for the new expiry (ATM defaults).
      return;
    }
    const cubeS = SURFACE[`${expiry}.${shortTen}`];
    const cubeL = SURFACE[`${expiry}.${longTen}`];
    if (!cubeS || !cubeL) return;
    if (legView === 'short'){
      setK_L_req(+(cubeS.atm_fwd_bp + spreadLvl).toFixed(2));
    } else {
      setK_S_req(+(cubeL.atm_fwd_bp - spreadLvl).toFixed(2));
    }
  }, [legView, expiry, shortTen, longTen, expDateLocked]);

  // On tenor / expiry change, default the strikes to ATM (forward) of each leg.
  // This keeps the "free-form" inputs initialised sensibly without forcing the user
  // to type something every time they switch tenors.
  useEffect(() => {
    const cubeS = SURFACE[`${expiry}.${shortTen}`];
    const cubeL = SURFACE[`${expiry}.${longTen}`];
    if (cubeS && cubeL){
      setK_S_req(+cubeS.atm_fwd_bp.toFixed(1));
      setK_L_req(+cubeL.atm_fwd_bp.toFixed(1));
    }
  }, [shortTen, longTen, expiry]);

  // Position
  const pos = useMemo(
    () => (K_S_req != null && K_L_req != null)
      ? buildCurvePosition(strategy, shortTen, longTen, expiry, K_S_req, K_L_req)
      : null,
    [strategy, shortTen, longTen, expiry, K_S_req, K_L_req]
  );

  // Default the target to forward spread on first build / re-default on key change
  useEffect(() => { if (pos && target == null) setTarget(+pos.fwdSpread.toFixed(1)); }, [pos, target]);
  useEffect(() => {
    if (expFromClick.current) { expFromClick.current = false; return; }
    setTarget(null);
  }, [shortTen, longTen, expiry]);
  useEffect(() => { setCustomExpDate(''); }, [shortTen, longTen]);

  const history = useMemo(() => {
    const full = buildSpreadHistory(shortTen, longTen);
    const winDays = WINDOW_DAYS[historyWindow] || WINDOW_DAYS['2Y'];
    const startIdx = Math.max(0, full.length - winDays);
    return full.slice(startIdx);
  }, [shortTen, longTen, historyWindow]);
  const fwdPts  = useMemo(() => buildSpreadForwards(shortTen, longTen), [shortTen, longTen]);

  // Which date (if any) is hovered on the scatter — shared across all three charts.
  const [scatterHoverDate, setScatterHoverDate] = useState(null);

  // Sizing → dollars
  const N_L = notionalL * 1e6;
  const N_S = N_L * (pos ? pos.ratio : 0);
  // 1 bp/DV01 of P&L = bp_per_DV01 × DV01_L × N_L × 1e-4 dollars.
  // (DV01 itself is already a bp-per-bp sensitivity for $1 of notional, expressed in bp units.)
  const dollarPerBpPnl = pos ? pos.dv01_L * N_L * 1e-4 : 0;
  const netPremium$ = pos ? pos.netPrem * dollarPerBpPnl : 0;

  // Scenario at user's target spread, assuming r_S lands at its forward
  const targetPnlBp = (pos && target != null) ? curvePnlPerDV01(pos, pos.F_S, pos.F_S + target) : null;
  const targetPnl$  = targetPnlBp != null ? targetPnlBp * dollarPerBpPnl : null;

  const validLongs = TENORS.filter(t => t !== shortTen);

  const lastHistDate = HISTORY[HISTORY.length-1].date;
  const expiryDateStr = addDays(lastHistDate, EXPIRY_DAYS[expiry]);
  const expiryFaintDate = customExpDate || expiryDateStr;

  return (
    <div style={{ ...S.pageContentFull, background: C.bg, color: C.tx }}>
      {/* === LEFT: controls + charts + backtest (scrollable) === */}
      <div style={{ flex:1, display:'flex', flexDirection:'column', borderRight:`1px solid ${C.bd}`, overflow:'auto' }}>
        {/* Tenor pair + expiry */}
        <div style={{ ...S.stripPrimary, gap:14, flexShrink:0 }}>
          <div style={{display:'flex', alignItems:'center', gap:6}}>
            <label style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em'}}>Short</label>
            <select value={shortTen} onChange={e => { const v = e.target.value; setShortTen(v); if (v === longTen) setLongTen(TENORS.find(t => t !== v)); }}
                    style={{background:C.bg, color:C.tx, border:`1px solid ${C.bd}`, borderRadius:3, padding:'3px 8px', fontSize:F.lg, fontFamily:'IBM Plex Mono, monospace'}}>
              {TENORS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{fontSize:F.h2, color:C.txD}}>vs</div>
          <div style={{display:'flex', alignItems:'center', gap:6}}>
            <label style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em'}}>Long</label>
            <select value={longTen} onChange={e => setLongTen(e.target.value)}
                    style={{background:C.bg, color:C.tx, border:`1px solid ${C.bd}`, borderRadius:3, padding:'3px 8px', fontSize:F.lg, fontFamily:'IBM Plex Mono, monospace'}}>
              {validLongs.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div style={{width:1, height:20, background:C.bd, margin:'0 4px'}}/>
          <div style={{display:'flex', alignItems:'center', gap:6}}>
            <label style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em'}}>Expiry</label>
            {EXPIRIES.map(e => (
              <button key={e} onClick={() => { setExpiry(e); setCustomExpDate(''); }}
                style={{ ...btn(!customExpDate && expiry===e, C.ac, C.acD), opacity: customExpDate ? 0.4 : 1 }}>
                {e}
              </button>
            ))}
            <input
              type="date"
              value={customExpDate || expiryDateStr}
              min={addDays(lastHistDate, 1)}
              max={addDays(lastHistDate, 400)}
              onChange={ev => {
                const d = ev.target.value;
                if (!d) return;
                setCustomExpDate(d);
                const exp = nearestExpiry(d, lastHistDate, EXPIRIES);
                expFromClick.current = true;
                if (exp) setExpiry(exp);
              }}
              style={{fontSize:F.sm, background: customExpDate ? C.acD : C.bg,
                      color: customExpDate ? C.ac : C.txD,
                      border:`1px solid ${customExpDate ? C.ac : C.bd}`,
                      borderRadius:3, padding:'2px 4px', fontFamily:'IBM Plex Mono, monospace',
                      colorScheme:'dark', cursor:'pointer'}}
            />
            {customExpDate && (
              <button onClick={() => setCustomExpDate('')}
                      style={{background:'transparent', border:'none', color:C.txD,
                              cursor:'pointer', fontSize:F.lg, padding:'0 2px'}}>✕</button>
            )}
            {/* Expiry lock — prevents chart clicks from changing the expiry date */}
            <button onClick={() => setExpDateLocked(l => !l)}
              title={expDateLocked ? 'Unlock: chart clicks will update the expiry date' : 'Lock: chart clicks won\'t change the expiry date'}
              style={{background: expDateLocked ? C.acD : 'transparent', border:`1px solid ${expDateLocked ? C.ac : C.bd}`,
                      borderRadius:2, padding:'2px 5px', color: expDateLocked ? C.ac : C.txD,
                      cursor:'pointer', display:'flex', alignItems:'center'}}>
              {expDateLocked
                ? <svg width="12" height="14" viewBox="0 0 11 13" fill="none"><rect x="1" y="5.5" width="9" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M3.5 5.5V3.5a2 2 0 0 1 4 0V5.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
                : <svg width="12" height="14" viewBox="0 0 11 13" fill="none"><rect x="1" y="5.5" width="9" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.5"/><path d="M3.5 5.5V3.5a2 2 0 0 1 4 0" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
              }
            </button>
          </div>
          <div style={{flex:1}}/>
          <div style={{fontSize:F.sm, color:C.txD}}>
            current spread: <span className="mono" style={{color:C.pu, fontWeight:700, marginLeft:4}}>
              {pos ? pos.fwdSpread.toFixed(1)+'bp' : '—'}
            </span>
          </div>
        </div>

        {/* History row — spread chart + single-tenor chart side by side */}
        <div style={{borderBottom:`1px solid ${C.bd}`, padding:'6px 6px 0 6px', flexShrink:0}}>
          {/* Global toolbar: history window only (leg selector moved to right-chart title) */}
          <div style={{padding:'0 8px 6px 8px', display:'flex', gap:8, alignItems:'center', flexWrap:'wrap'}}>
            <span style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em'}}>Window</span>
            {Object.keys(WINDOW_DAYS).map(w => (
              <button key={w} onClick={() => setHistoryWindow(w)} style={btn(w===historyWindow, C.ac, C.acD)}>{w}</button>
            ))}
            <div style={{flex:1}}/>
            <span style={{fontSize:F.xs, color:C.txM, fontStyle:'italic'}}>
              spread chart sets target + <span style={{color: legView==='short'?C.gn:C.bl}}>{legView==='short'?longTen:shortTen}</span> strike
              {' · '}rate chart sets <span style={{color: legView==='short'?C.bl:C.gn}}>{legView==='short'?shortTen:longTen}</span> strike
            </span>
          </div>
          {/* Side-by-side charts */}
          <div style={{display:'flex', gap:8}}>
            <div style={{flex:1, minWidth:0}}>
              <div style={{padding:'0 8px 4px 8px', display:'flex', alignItems:'center',
                            gap:10, justifyContent:'space-between'}}>
                <span style={{fontSize:F.xs, color:C.pu,
                               textTransform:'uppercase', letterSpacing:'.08em', fontWeight:700}}>
                  Spread — {longTen} − {shortTen}
                </span>
                <span style={{fontSize:F.xs, color:C.txM, fontStyle:'italic'}}>
                  target {target != null ? `${target.toFixed(1)}bp` : '—'}
                </span>
              </div>
              <SpreadHistoryChart shortTen={shortTen} longTen={longTen}
                                  target={target} onDrag={onDragSpread}
                                  fwdPoints={fwdPts} historyWindow={historyWindow}
                                  highlightDate={scatterHoverDate}
                                  expiryDate={expiryFaintDate}/>
            </div>
            <div style={{flex:1, minWidth:0, borderLeft:`1px solid ${C.bd}`, paddingLeft:8}}>
              {/* Right-chart title row: tenor toggle lives here so it's visually bound to this chart */}
              <div style={{padding:'0 8px 4px 8px', display:'flex', alignItems:'center', gap:8, justifyContent:'space-between'}}>
                <div style={{display:'flex', alignItems:'center', gap:6}}>
                  <span style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.08em'}}>
                    Rate of
                  </span>
                  {[['short', shortTen], ['long', longTen]].map(([k, label]) => {
                    const isActive = k === legView;
                    const col = k === 'short' ? C.bl : C.gn;
                    return (
                      <button key={k} onClick={() => setLegView(k)}
                              style={{background: isActive ? `${col}22` : 'transparent',
                                      border:`1px solid ${isActive ? col : C.bd}`,
                                      borderRadius:3, padding:'3px 9px', fontSize:F.md, cursor:'pointer',
                                      color: isActive ? col : C.txD,
                                      fontFamily:'IBM Plex Mono, monospace', fontWeight:700}}>
                        {label}
                      </button>
                    );
                  })}
                </div>
                <span style={{fontSize:F.xs, color: legView==='short' ? C.bl : C.gn, fontWeight:600}}>
                  K = {pos ? (legView==='short'?pos.K_S:pos.K_L).toFixed(1)+'bp' : '—'}
                </span>
              </div>
              <RateHistoryChart tenor={legView==='short' ? shortTen : longTen}
                                strikeAbs={pos ? (legView==='short' ? pos.K_S : pos.K_L) : null}
                                fwdAbs={pos ? (legView==='short' ? pos.F_S : pos.F_L) : null}
                                expiry={expiry}
                                onDragStrike={onDragStrike}
                                historyWindow={historyWindow}
                                accent={legView==='short' ? C.bl : C.gn}
                                highlightDate={scatterHoverDate}
                                expiryDate={expiryFaintDate}/>
            </div>
          </div>
        </div>

        {/* Scatter chart */}
        <div style={{padding:'8px 6px', flexShrink:0}}>
          <div style={{padding:'0 8px 4px 8px', fontSize:F.xs, color:C.txD,
                       textTransform:'uppercase', letterSpacing:'.08em',
                       display:'flex', justifyContent:'space-between'}}>
            <span>{shortTen} rate × spread — payoff at {expiry} expiry</span>
            <span style={{color:pos?.def.color || C.txM, textTransform:'none', letterSpacing:0, fontStyle:'italic'}}>
              {pos ? `breakeven (${pos.def.label})` : ''}
            </span>
          </div>
          <CurveScatterChart pos={pos} history={history} targetSpread={target}
                             onHoverDate={setScatterHoverDate}
                             highlightDate={scatterHoverDate}/>
        </div>

        {/* Backtest table */}
        <CurveBacktestTable pos={pos} dollarPerBpPnl={dollarPerBpPnl}/>
      </div>

      {/* === RIGHT: trade summary === */}
      <div style={{ ...S.rightPanel, width: PANEL_W }}>
        {/* Strategy selector — 2×2 card grid */}
        <div style={{padding:'10px 16px', borderBottom:`1px solid ${C.bd}`, background:C.bg}}>
          <div style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.1em', marginBottom:8}}>Strategy</div>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:6}}>
            {Object.entries(CURVE_STRATEGIES).map(([k,d]) => {
              const active = strategy === k;
              return (
                <button key={k} onClick={() => setStrategy(k)}
                  style={{textAlign:'left', cursor:'pointer',
                          background: active ? `${d.color}18` : 'transparent',
                          border:`1px solid ${active ? d.color : C.bd}`,
                          borderLeft:`3px solid ${d.color}`,
                          borderRadius:3, padding:'8px 10px'}}>
                  <div style={{fontSize:F.sm, fontWeight:700, color: active ? d.color : C.tx, lineHeight:1.2}}>{d.label}</div>
                  <div style={{fontSize:F.xxs, color:C.txD, marginTop:3, lineHeight:1.3}}>{d.sub}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* === PREMIUM AT INCEPTION (featured card) === */}
        <div style={{padding:'14px 16px', borderBottom:`1px solid ${C.bd}`,
                      background: pos && pos.netPrem >= 0 ? 'rgba(248,81,73,.05)' : 'rgba(63,185,80,.05)',
                      borderLeft: `3px solid ${pos && pos.netPrem >= 0 ? C.rd : C.gn}`}}>
          <div style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.1em', marginBottom:4,
                        display:'flex', justifyContent:'space-between'}}>
            <span>Premium at inception</span>
            <span style={{color:C.txM, textTransform:'none', letterSpacing:0, fontStyle:'italic'}}>
              {pos && pos.netPrem >= 0 ? 'debit (you pay)' : 'credit (you receive)'}
            </span>
          </div>
          <div style={{display:'flex', alignItems:'baseline', justifyContent:'space-between'}}>
            <div className="mono" style={{fontSize:F.d1, fontWeight:700,
                                            color: pos && pos.netPrem >= 0 ? C.rd : C.gn}}>
              {pos ? (pos.netPrem >= 0 ? '−$' : '+$') + Math.abs(netPremium$).toLocaleString('en-US',{maximumFractionDigits:0}) : '—'}
            </div>
            <div className="mono" style={{fontSize:F.lg, color:C.txD}}>
              {pos ? (pos.netPrem >= 0 ? '−' : '+') + Math.abs(pos.netPrem).toFixed(2) + ' bp/DV01' : ''}
            </div>
          </div>
          <div style={{fontSize:F.xs, color:C.txM, marginTop:4, display:'flex', justifyContent:'space-between'}}>
            <span>at {longTen}={notionalL}m</span>
            <span>{pos ? `short prem ${pos.prem_S.toFixed(2)}, long prem ${pos.prem_L.toFixed(2)}` : ''}</span>
          </div>
        </div>

        {/* Strikes — free-form */}
        <div style={{padding:'12px 16px', borderBottom:`1px solid ${C.bd}`}}>
          <div style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.1em', marginBottom:8}}>Strikes</div>
          <div style={{display:'flex', flexDirection:'column', gap:8}}>
            <StrikeInput label={`K ${shortTen}`}
                         value={K_S_req}
                         atm={pos?.F_S}
                         onChange={v => { setK_S_req(v); if (target != null) setK_L_req(+(v + target).toFixed(2)); }}/>
            <StrikeInput label={`K ${longTen}`}
                         value={K_L_req}
                         atm={pos?.F_L}
                         onChange={v => { setK_L_req(v); if (target != null) setK_S_req(+(v - target).toFixed(2)); }}/>
          </div>
        </div>

        {/* Sizing */}
        <div style={{padding:'12px 16px', borderBottom:`1px solid ${C.bd}`}}>
          <div style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.1em', marginBottom:6}}>Sizing</div>
          <div style={{display:'flex', alignItems:'center', gap:6, marginBottom:8}}>
            <label style={{fontSize:F.sm, color:C.txD}}>{longTen} notional</label>
            <input type="number" value={notionalL} min="1" max="10000"
                   onChange={e => { const v = parseFloat(e.target.value); if (!isNaN(v) && v > 0) setNotionalL(v); }}
                   style={{background:C.bg, border:`1px solid ${C.bd}`, borderRadius:3, padding:'3px 6px',
                           color:C.tx, fontSize:F.lg, width:80, fontFamily:'IBM Plex Mono, monospace', outline:'none'}}/>
            <span style={{fontSize:F.sm, color:C.txD}}>m</span>
          </div>
          <div style={{display:'grid', gridTemplateColumns:'auto 1fr', gap:'3px 10px', fontSize:F.md}}>
            <span style={{color:C.txD, fontSize:F.sm}}>{shortTen} notional</span>
            <span className="mono" style={{color:C.tx, fontWeight:600, textAlign:'right'}}>
              {pos ? (notionalL * pos.ratio).toFixed(0) : '—'}m  <span style={{color:C.txD, fontWeight:400}}>(× {pos ? pos.ratio.toFixed(2) : '—'})</span>
            </span>
            <span style={{color:C.txD, fontSize:F.sm}}>{shortTen} DV01</span>
            <span className="mono" style={{color:C.tx, textAlign:'right'}}>{pos ? pos.dv01_S.toFixed(2) : '—'}</span>
            <span style={{color:C.txD, fontSize:F.sm}}>{longTen} DV01</span>
            <span className="mono" style={{color:C.tx, textAlign:'right'}}>{pos ? pos.dv01_L.toFixed(2) : '—'}</span>
          </div>
        </div>

        {/* Legs */}
        <div style={{padding:'12px 16px', borderBottom:`1px solid ${C.bd}`}}>
          <div style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.1em', marginBottom:6}}>Legs</div>
          {pos && (
            <div style={{display:'grid', gridTemplateColumns:'auto 1fr auto', gap:'5px 8px', fontSize:F.md, alignItems:'center'}}>
              {[['short', shortTen, pos.short, pos.K_S, pos.prem_S, pos.vol_S],
                ['long',  longTen,  pos.long,  pos.K_L, pos.prem_L, pos.vol_L]].map(([slot, ten, leg, K, prem, vol]) => (
                <React.Fragment key={slot}>
                  <span style={{color: leg.side > 0 ? C.gn : C.rd, fontWeight:700, fontSize:F.lg, fontFamily:'IBM Plex Mono, monospace'}}>
                    {leg.side > 0 ? '+' : '−'}
                  </span>
                  <span style={{color:C.tx, fontSize:F.md}}>
                    {ten} {leg.isPayer ? 'payer' : 'receiver'} @ <span className="mono">{K.toFixed(1)}</span>
                  </span>
                  <span className="mono" style={{color:C.txD, fontSize:F.sm, textAlign:'right'}}>
                    σ={vol.toFixed(1)} · prem={prem.toFixed(2)}
                  </span>
                </React.Fragment>
              ))}
            </div>
          )}
        </div>

        {/* Target scenario */}
        <div style={{padding:'12px 16px', borderBottom:`1px solid ${C.bd}`}}>
          <div style={{fontSize:F.xs, color:C.txD, textTransform:'uppercase', letterSpacing:'.1em', marginBottom:6}}>Target scenario</div>
          <div style={{display:'grid', gridTemplateColumns:'1fr 1fr', gap:'4px 10px', fontSize:F.md}}>
            <span style={{color:C.txD, fontSize:F.sm}}>Target spread</span>
            <span className="mono" style={{color:C.pu, fontWeight:700, textAlign:'right'}}>
              {target != null ? target.toFixed(1)+' bp' : '—'}
            </span>
            <span style={{color:C.txD, fontSize:F.sm}}>Forward spread</span>
            <span className="mono" style={{color:C.txD, fontWeight:500, textAlign:'right'}}>
              {pos ? pos.fwdSpread.toFixed(1)+' bp' : '—'}
            </span>
            <span style={{color:C.txD, fontSize:F.sm}}>Δ vs forward</span>
            <span className="mono" style={{color:C.am, fontWeight:600, textAlign:'right'}}>
              {(pos && target != null) ? ((target - pos.fwdSpread) >= 0 ? '+' : '') + (target - pos.fwdSpread).toFixed(1)+' bp' : '—'}
            </span>
          </div>
          <div style={{marginTop:8, paddingTop:8, borderTop:`1px dashed ${C.bd}`,
                        display:'grid', gridTemplateColumns:'1fr 1fr', gap:'3px 10px', fontSize:F.md}}>
            <span style={{color:C.txD, fontSize:F.sm, fontStyle:'italic'}}>assuming {shortTen}=fwd</span>
            <span/>
            <span style={{color:C.txD, fontSize:F.sm}}>P&L per DV01</span>
            <span className="mono" style={{color: targetPnlBp != null && targetPnlBp >= 0 ? C.gn : C.rd, fontWeight:700, textAlign:'right'}}>
              {targetPnlBp != null ? (targetPnlBp >= 0 ? '+' : '') + targetPnlBp.toFixed(2)+' bp' : '—'}
            </span>
            <span style={{color:C.txD, fontSize:F.sm}}>P&L $ at sized trade</span>
            <span className="mono" style={{color: targetPnl$ != null && targetPnl$ >= 0 ? C.gn : C.rd, fontWeight:700, fontSize:F.lg, textAlign:'right'}}>
              {targetPnl$ != null ? (targetPnl$ >= 0 ? '+$' : '−$') + Math.abs(targetPnl$).toLocaleString('en-US',{maximumFractionDigits:0}) : '—'}
            </span>
          </div>
        </div>

        <div style={{flex:1}}/>
        <div style={{padding:'8px 16px', borderTop:`1px solid ${C.bd}`, fontSize:F.xs,
                      color:C.txM, fontStyle:'italic', background:C.bg}}>
          DV01-weighted sizing makes the position neutral to parallel rate moves, isolating curve view.
          The dashed diagonal is where r_{longTen} = K_{longTen}; the strategy-coloured curve is the breakeven.
        </div>
      </div>
    </div>
  );
}
